/**
 * Stripe Provider Wrapper Component
 * Location: src/components/stripe/StripeProvider.tsx
 * 
 * This component wraps your app (or billing routes) with Stripe Elements context
 */

import React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';

// Load Stripe based on environment mode
const getStripePublishableKey = (): string => {
  const mode = import.meta.env.VITE_STRIPE_MODE || 'test';
  
  if (mode === 'live') {
    const liveKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_LIVE;
    if (!liveKey) {
      throw new Error('Stripe live publishable key is missing');
    }
    return liveKey;
  }
  
  const testKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_TEST;
  if (!testKey) {
    throw new Error('Stripe test publishable key is missing');
  }
  return testKey;
};

// Initialize Stripe - this happens once per app load
const stripePublishableKey = getStripePublishableKey();
const stripePromise: Promise<Stripe | null> = loadStripe(stripePublishableKey);

// Log current mode for debugging (remove in production)
const currentMode = import.meta.env.VITE_STRIPE_MODE || 'test';

interface StripeProviderProps {
  children: React.ReactNode;
}

export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  return (
    <Elements stripe={stripePromise}>
      {children}
    </Elements>
  );
};

// Export for components that need direct stripe instance
export { stripePromise };